import { useCallback, useEffect, useRef, useState } from "react";
import { requestConnectToken } from "@/lib/connect-token";

/**
 * Connect token for `projectId`, minted on demand by `generate()` — each
 * call creates a runner token row, so never mint on render. Resets when
 * the project changes. `initial` seeds a fixed token (demo mode).
 */
export function useConnectToken(
  projectId: string | null,
  initial: string | null = null,
): {
  token: string | null;
  error: boolean;
  loading: boolean;
  generate: () => void;
} {
  const [token, setToken] = useState<string | null>(initial);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  // A ref, not `loading`: two calls in one render (StrictMode effects)
  // would both see loading=false and mint twice.
  const inFlight = useRef(false);

  // Reset token when project changes
  useEffect(() => {
    if (initial) return;
    setToken(null);
    setError(false);
  }, [projectId, initial]);

  const generate = useCallback(() => {
    if (initial || !projectId || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(false);
    requestConnectToken(projectId)
      .then((t) => {
        setToken(t);
        setError(!t);
      })
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, [projectId, initial]);

  return { token, error, loading, generate };
}
